import type { CSSProperties } from "react";
import React, { useCallback, useContext, useMemo } from "react";

import type { BrazeOperatorContextType } from "@lib/BrazeOperatorContext";
import { BrazeOperatorContext } from "@lib/BrazeOperatorContext";
import type { AgenticAiRegisterTool } from "@lib/agenticAi/types";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import { useIsOperatorDebugModeEnabled } from "@services/useIsOperatorDebugModeEnabled";

import type { BrazeOperatorProviderProps } from "./types";

/**
 * Provider component that enables Operator to make changes to components agentically.
 *
 * When `enabled` is true, operator-aware components will register tools available to Operator to make changes to components agentically.
 *
 * This provider only manages operator change-tool enablement. Composer context (the Liquid
 * syntax checker, composer instructions, etc.) is registered separately via the
 * `useComposerContext` hook, called as a sibling alongside this provider on composer pages.
 *
 * @example
 * // Always enable Operator to make changes to components agentically for this subtree
 * <BrazeOperatorProvider enabled={true}>
 *   <NameDescription />
 * </BrazeOperatorProvider>
 *
 * @example
 * // Conditionally enable Operator to make changes to components agentically based on some condition
 * <BrazeOperatorProvider enabled={isSmsComposer}>
 *   <NameDescription />
 * </BrazeOperatorProvider>
 */
export const BrazeOperatorProvider = ({
  children,
  enabled,
  suppressDescendantProviders,
  registerToolOverride,
  featureName,
}: BrazeOperatorProviderProps) => {
  const { isOperatorSuppressed: isOperatorSuppressedByAncestor } = useContext(BrazeOperatorContext);
  const { registerTool: contextRegisterTool } = useAgenticAiContext();
  const registerTool = registerToolOverride ?? contextRegisterTool;
  const isOperatorDebugModeEnabled = useIsOperatorDebugModeEnabled();
  const isOperatorSuppressed = isOperatorSuppressedByAncestor === true || suppressDescendantProviders === true;
  const isAgenticChangeEnabled = enabled && !isOperatorSuppressed;

  const registerChangeTool: AgenticAiRegisterTool = useCallback(
    (params) => {
      if (!isAgenticChangeEnabled) {
        return () => {};
      }
      return registerTool(params);
    },
    [isAgenticChangeEnabled, registerTool]
  );

  const value: BrazeOperatorContextType = useMemo(
    () => ({
      isOperatorSuppressed,
      isAgenticChangeEnabled,
      registerChangeTool,
    }),
    [isAgenticChangeEnabled, isOperatorSuppressed, registerChangeTool]
  );

  // In Operator debug mode, wrap children in a layout-neutral (display: contents)
  // element tagged with data-operator-provider so the debug highlight can outline
  // which subtrees a BrazeOperatorProvider covers. The wrapper — and the style
  // object below — are only built while debugging, so production renders fall
  // straight through to `children` with no extra DOM and no wasted work. The
  // attribute value records whether this provider is actively registering
  // change-tools.
  //
  // When the provider was gated on a named flipper, also expose that name: the
  // data-operator-provider-feature attribute lets the highlight CSS select these
  // regions, and the --operator-provider-ff custom property carries the name as a
  // CSS string for an ::after label. A custom property is used (rather than reading
  // the attribute via attr()) because it inherits down to the outlined child
  // through the display: contents wrapper, which attr() cannot do. Feature names
  // are [a-z0-9_], so embedding one in a quoted CSS string needs no escaping.
  const wrappedChildren = isOperatorDebugModeEnabled ? (
    <div
      data-operator-provider={isAgenticChangeEnabled ? "enabled" : "disabled"}
      data-operator-provider-feature={featureName}
      style={
        featureName
          ? ({ display: "contents", "--operator-provider-ff": `"${featureName}"` } as CSSProperties)
          : { display: "contents" }
      }
    >
      {children}
    </div>
  ) : (
    children
  );

  return <BrazeOperatorContext.Provider value={value}>{wrappedChildren}</BrazeOperatorContext.Provider>;
};

BrazeOperatorProvider.displayName = "BrazeOperatorProvider";
